import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Avatar, { getPersonPhotoUrl } from "./Avatar";
import {
  ORG_CHART_ZOOM_DEFAULT,
  ORG_CHART_ZOOM_MAX,
  ORG_CHART_ZOOM_MIN,
  ORG_CHART_ZOOM_STEP,
  cleanOrgChartZoom,
  readOrgChartZoom,
  saveOrgChartZoom,
} from "../lib/orgChartZoom";
import {
  ORG_CHART_DIMENSIONS,
  getDefaultOrgChartPosition,
  getDirectReports,
  getOrgChartLayoutMetrics,
  getTeamSecurityLabel,
} from "../lib/teamDirectory";

const SNAP_THRESHOLD = 10;
const SPACING_THRESHOLD = 14;
const GUIDE_PADDING = 14;
const SPACING_TICK_SIZE = 7;
const MIN_SPACING_OVERLAP = 30;
const DRAG_PERSIST_INTERVAL_MS = 180;
const MARQUEE_START_THRESHOLD = 4;
const CONNECTOR_CLEARANCE = 18;
const CONNECTOR_STUB = 20;
const CONNECTOR_CORNER_RADIUS = 14;

function getMemberLabel(member) {
  return String(member?.name || member?.email || "Unnamed teammate").trim();
}

function getTimeZoneLabel(tz) {
  const value = String(tz || "").trim();
  if (!value) return "No timezone";
  if (value === "Etc/UTC") return "UTC";
  const parts = value.split("/").filter(Boolean);
  if (parts.length <= 1) return value.replaceAll("_", " ");
  return parts.slice(1).join(" / ").replaceAll("_", " ");
}

function getAlignmentAnchors(position, metrics) {
  return {
    left: position.x,
    centerX: position.x + metrics.nodeWidth / 2,
    right: position.x + metrics.nodeWidth,
    top: position.y,
    centerY: position.y + metrics.nodeHeight / 2,
    bottom: position.y + metrics.nodeHeight,
  };
}

function clampNodePosition(position = {}, canvasSize, metrics) {
  const maxX = canvasSize.width - metrics.nodeWidth - metrics.paddingX;
  const maxY = canvasSize.height - metrics.nodeHeight - metrics.paddingY;
  return {
    x: Math.max(
      metrics.paddingX,
      Math.min(maxX, Number(position?.x) || metrics.paddingX),
    ),
    y: Math.max(
      metrics.paddingY,
      Math.min(maxY, Number(position?.y) || metrics.paddingY),
    ),
  };
}

function positionsMatch(left, right) {
  return Number(left?.x) === Number(right?.x) && Number(left?.y) === Number(right?.y);
}

// In the chart's own coordinates, whatever it's zoomed to: the rect is what's on screen.
function getCanvasPoint(event, element, zoom = 1) {
  const rect = element?.getBoundingClientRect?.();
  if (!rect) return { x: 0, y: 0 };
  return {
    x: (event.clientX - rect.left) / zoom,
    y: (event.clientY - rect.top) / zoom,
  };
}

function normalizeRect(start, end) {
  return {
    left: Math.min(start.x, end.x),
    top: Math.min(start.y, end.y),
    right: Math.max(start.x, end.x),
    bottom: Math.max(start.y, end.y),
  };
}

function rectsOverlap(left, right) {
  return (
    left.left <= right.right &&
    left.right >= right.left &&
    left.top <= right.bottom &&
    left.bottom >= right.top
  );
}

function isMultiSelectGesture(event) {
  return !!(event?.metaKey || event?.ctrlKey || event?.shiftKey);
}

function clampGroupDelta(delta, originsById, canvasSize, metrics) {
  const entries = Object.values(originsById || {});
  if (!entries.length) return delta;
  const maxX = canvasSize.width - metrics.nodeWidth - metrics.paddingX;
  const maxY = canvasSize.height - metrics.nodeHeight - metrics.paddingY;
  const minDeltaX = Math.max(
    ...entries.map((position) => metrics.paddingX - position.x),
  );
  const maxDeltaX = Math.min(...entries.map((position) => maxX - position.x));
  const minDeltaY = Math.max(
    ...entries.map((position) => metrics.paddingY - position.y),
  );
  const maxDeltaY = Math.min(...entries.map((position) => maxY - position.y));

  return {
    x: Math.max(minDeltaX, Math.min(maxDeltaX, delta.x)),
    y: Math.max(minDeltaY, Math.min(maxDeltaY, delta.y)),
  };
}

function createConnectorObstacle(memberId, position, metrics) {
  return {
    id: memberId,
    left: position.x - CONNECTOR_CLEARANCE,
    top: position.y - CONNECTOR_CLEARANCE,
    right: position.x + metrics.nodeWidth + CONNECTOR_CLEARANCE,
    bottom: position.y + metrics.nodeHeight + CONNECTOR_CLEARANCE,
  };
}

function getConnectorAnchor(position, metrics, side) {
  const centerX = position.x + metrics.nodeWidth / 2;
  const centerY = position.y + metrics.nodeHeight / 2;
  switch (side) {
    case "top":
      return { x: centerX, y: position.y };
    case "bottom":
      return { x: centerX, y: position.y + metrics.nodeHeight };
    case "left":
      return { x: position.x, y: centerY };
    case "right":
    default:
      return { x: position.x + metrics.nodeWidth, y: centerY };
  }
}

function offsetConnectorPoint(point, side, amount) {
  switch (side) {
    case "top":
      return { x: point.x, y: point.y - amount };
    case "bottom":
      return { x: point.x, y: point.y + amount };
    case "left":
      return { x: point.x - amount, y: point.y };
    case "right":
    default:
      return { x: point.x + amount, y: point.y };
  }
}

function getConnectorSidePairs(sourcePos, targetPos, metrics) {
  const sourceCenter = {
    x: sourcePos.x + metrics.nodeWidth / 2,
    y: sourcePos.y + metrics.nodeHeight / 2,
  };
  const targetCenter = {
    x: targetPos.x + metrics.nodeWidth / 2,
    y: targetPos.y + metrics.nodeHeight / 2,
  };
  const dy = targetCenter.y - sourceCenter.y;
  const clearlyVertical = Math.abs(dy) >= metrics.nodeHeight * 0.35;
  const preferredPairs = clearlyVertical
    ? dy >= 0
      ? [
          ["bottom", "top"],
          ["bottom", "bottom"],
          ["top", "top"],
        ]
      : [
          ["top", "bottom"],
          ["top", "top"],
          ["bottom", "bottom"],
        ]
    : [
        ["bottom", "bottom"],
        ["top", "top"],
        ["bottom", "top"],
        ["top", "bottom"],
      ];

  return preferredPairs.filter(
    (pair, index, source) =>
      source.findIndex(
        (candidate) =>
          candidate[0] === pair[0] && candidate[1] === pair[1],
      ) === index,
  );
}

function compressOrthogonalPoints(points = []) {
  const compact = [];
  points.forEach((point) => {
    if (!point) return;
    const normalized = {
      x: Math.round(point.x * 100) / 100,
      y: Math.round(point.y * 100) / 100,
    };
    const last = compact[compact.length - 1];
    if (last && positionsMatch(last, normalized)) return;
    compact.push(normalized);
    while (compact.length >= 3) {
      const a = compact[compact.length - 3];
      const b = compact[compact.length - 2];
      const c = compact[compact.length - 1];
      const sameColumn =
        Number(a.x) === Number(b.x) && Number(b.x) === Number(c.x);
      const sameRow =
        Number(a.y) === Number(b.y) && Number(b.y) === Number(c.y);
      if (!sameColumn && !sameRow) break;
      compact.splice(compact.length - 2, 1);
    }
  });
  return compact;
}

function segmentIntersectsObstacle(start, end, obstacle) {
  if (Number(start.x) === Number(end.x)) {
    const x = start.x;
    const top = Math.min(start.y, end.y);
    const bottom = Math.max(start.y, end.y);
    return (
      x > obstacle.left &&
      x < obstacle.right &&
      bottom > obstacle.top &&
      top < obstacle.bottom
    );
  }

  if (Number(start.y) === Number(end.y)) {
    const y = start.y;
    const left = Math.min(start.x, end.x);
    const right = Math.max(start.x, end.x);
    return (
      y > obstacle.top &&
      y < obstacle.bottom &&
      right > obstacle.left &&
      left < obstacle.right
    );
  }

  return true;
}

function isOrthogonalPathClear(points, obstacles = []) {
  const compact = compressOrthogonalPoints(points);
  for (let index = 1; index < compact.length; index += 1) {
    const start = compact[index - 1];
    const end = compact[index];
    if (
      Number(start.x) !== Number(end.x) &&
      Number(start.y) !== Number(end.y)
    ) {
      return false;
    }
    if (obstacles.some((obstacle) => segmentIntersectsObstacle(start, end, obstacle))) {
      return false;
    }
  }
  return true;
}

function getConnectorPathScore(points = []) {
  const compact = compressOrthogonalPoints(points);
  if (compact.length < 2) return Number.POSITIVE_INFINITY;
  const length = compact.slice(1).reduce((sum, point, index) => {
    const previous = compact[index];
    return sum + Math.abs(point.x - previous.x) + Math.abs(point.y - previous.y);
  }, 0);
  const bends = Math.max(0, compact.length - 2);
  return length + bends * 44;
}

function toSvgPath(points = []) {
  const compact = compressOrthogonalPoints(points);
  if (!compact.length) return "";
  if (compact.length === 1) return `M ${compact[0].x} ${compact[0].y}`;

  let d = `M ${compact[0].x} ${compact[0].y}`;
  for (let index = 1; index < compact.length; index += 1) {
    const current = compact[index];
    if (index === compact.length - 1) {
      d += ` L ${current.x} ${current.y}`;
      continue;
    }

    const previous = compact[index - 1];
    const next = compact[index + 1];
    const incomingLength =
      Math.abs(current.x - previous.x) + Math.abs(current.y - previous.y);
    const outgoingLength =
      Math.abs(next.x - current.x) + Math.abs(next.y - current.y);
    const radius = Math.min(
      CONNECTOR_CORNER_RADIUS,
      incomingLength / 2,
      outgoingLength / 2,
    );

    const before = {
      x:
        current.x -
        Math.sign(current.x - previous.x || 0) * radius,
      y:
        current.y -
        Math.sign(current.y - previous.y || 0) * radius,
    };
    const after = {
      x:
        current.x +
        Math.sign(next.x - current.x || 0) * radius,
      y:
        current.y +
        Math.sign(next.y - current.y || 0) * radius,
    };

    d += ` L ${before.x} ${before.y} Q ${current.x} ${current.y} ${after.x} ${after.y}`;
  }

  return d;
}

function buildConnectorPath(
  sourcePos,
  targetPos,
  metrics,
  obstacles = [],
  canvasSize,
) {
  const outerLaneX = Math.max(6, metrics.paddingX - CONNECTOR_CLEARANCE - 6);
  const outerLaneY = Math.max(6, metrics.paddingY - CONNECTOR_CLEARANCE - 6);
  const xCandidatesBase = [
    outerLaneX,
    (canvasSize?.width || ORG_CHART_DIMENSIONS.workspaceWidth) - outerLaneX,
    ...obstacles.flatMap((obstacle) => [obstacle.left, obstacle.right]),
  ];
  const yCandidatesBase = [
    outerLaneY,
    (canvasSize?.height || ORG_CHART_DIMENSIONS.workspaceHeight) - outerLaneY,
    ...obstacles.flatMap((obstacle) => [obstacle.top, obstacle.bottom]),
  ];
  const candidates = [];

  function addCandidate(points) {
    const compact = compressOrthogonalPoints(points);
    if (compact.length < 2) return;
    if (!isOrthogonalPathClear(compact, obstacles)) return;
    candidates.push(compact);
  }

  getConnectorSidePairs(sourcePos, targetPos, metrics).forEach(
    ([sourceSide, targetSide]) => {
      const start = getConnectorAnchor(sourcePos, metrics, sourceSide);
      const end = getConnectorAnchor(targetPos, metrics, targetSide);
      const startStub = offsetConnectorPoint(start, sourceSide, CONNECTOR_STUB);
      const endStub = offsetConnectorPoint(end, targetSide, CONNECTOR_STUB);
      const xCandidates = [
        startStub.x,
        endStub.x,
        Math.round((startStub.x + endStub.x) / 2),
        ...xCandidatesBase,
      ].filter(
        (value, index, source) =>
          Number.isFinite(value) &&
          source.findIndex((candidate) => Number(candidate) === Number(value)) ===
            index,
      );
      const yCandidates = [
        startStub.y,
        endStub.y,
        Math.round((startStub.y + endStub.y) / 2),
        ...yCandidatesBase,
      ].filter(
        (value, index, source) =>
          Number.isFinite(value) &&
          source.findIndex((candidate) => Number(candidate) === Number(value)) ===
            index,
      );

      addCandidate([start, startStub, endStub, end]);
      addCandidate([
        start,
        startStub,
        { x: startStub.x, y: endStub.y },
        endStub,
        end,
      ]);
      addCandidate([
        start,
        startStub,
        { x: endStub.x, y: startStub.y },
        endStub,
        end,
      ]);

      yCandidates.forEach((y) => {
        addCandidate([
          start,
          startStub,
          { x: startStub.x, y },
          { x: endStub.x, y },
          endStub,
          end,
        ]);
      });

      xCandidates.forEach((x) => {
        addCandidate([
          start,
          startStub,
          { x, y: startStub.y },
          { x, y: endStub.y },
          endStub,
          end,
        ]);
      });
    },
  );

  const bestPath =
    candidates.sort(
      (left, right) =>
        getConnectorPathScore(left) - getConnectorPathScore(right),
    )[0] || [];

  if (bestPath.length) return toSvgPath(bestPath);

  const goingDown =
    targetPos.y + metrics.nodeHeight / 2 >= sourcePos.y + metrics.nodeHeight / 2;
  const fallbackStart = getConnectorAnchor(
    sourcePos,
    metrics,
    goingDown ? "bottom" : "top",
  );
  const fallbackEnd = getConnectorAnchor(
    targetPos,
    metrics,
    goingDown ? "top" : "bottom",
  );
  const midwayY = Math.round((fallbackStart.y + fallbackEnd.y) / 2);
  return toSvgPath([
    fallbackStart,
    { x: fallbackStart.x, y: midwayY },
    { x: fallbackEnd.x, y: midwayY },
    fallbackEnd,
  ]);
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M4 16.8V20h3.2l8.9-8.9-3.2-3.2L4 16.8zm11.8-10.4l1.7-1.7a1.4 1.4 0 0 1 2 0l.8.8a1.4 1.4 0 0 1 0 2l-1.7 1.7-2.8-2.8z"
        fill="currentColor"
      />
    </svg>
  );
}

export default function TeamDirectoryOrgChart({
  teamMembers,
  selectedId,
  onSelect,
  onAutoLayout,
  onCommitPosition,
  onCommitPositions,
  onToggleReportingLine,
  onOpenCreate,
  onOpenEdit,
  onDeleteMember,
  onSendNow,
  sendingId,
  statusById,
  reconnectByEmail = {},
  apiBase = "",
  getOrgSummary,
  // Renamed from canManageWorkspace, and now defaulting to FALSE. Editing this
  // chart edits the Team directory, which is the workspace's access map, and
  // that is owner-only -- an admin manages the workspace but does not hand out
  // access. A caller that forgets the prop gets a read-only chart.
  canEditDirectory = false,
  // Owner OR admin (the caller passes canManageWorkspace): the exec report's
  // "Send now". Separate from canEditDirectory because sending a report is not
  // handing out access. Defaults FALSE, like every permission prop here.
  canSendReports = false,
  editableMemberIds = [],
}) {
  const markerIdRef = useRef(
    `org-arrow-${Math.random().toString(36).slice(2, 10)}`,
  );
  const canvasRef = useRef(null);
  const scrollerRef = useRef(null);
  const dragRef = useRef(null);
  const marqueeRef = useRef(null);
  const suppressClickRef = useRef(null);
  const persistedPositionsRef = useRef({});
  const lastDragPersistAtRef = useRef(0);
  const [draggingIds, setDraggingIds] = useState([]);
  const [linkSourceId, setLinkSourceId] = useState(null);
  const [alignmentGuides, setAlignmentGuides] = useState([]);
  const [marqueeRect, setMarqueeRect] = useState(null);
  const [zoomPercent, setZoomPercent] = useState(readOrgChartZoom);
  const zoom = zoomPercent / 100;
  const zoomRef = useRef(zoom);
  const zoomAnchorRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState(() =>
    selectedId ? [selectedId] : [],
  );
  const editableMemberIdSet = useMemo(
    () => new Set(editableMemberIds || []),
    [editableMemberIds],
  );
  const memberCount = teamMembers.length;
  const layoutMetrics = useMemo(
    () => getOrgChartLayoutMetrics(memberCount),
    [memberCount],
  );
  const [positions, setPositions] = useState(() =>
    Object.fromEntries(
      (teamMembers || []).map((member, index) => [
        member.id,
        member.orgChart || getDefaultOrgChartPosition(index, memberCount),
      ]),
    ),
  );
  const positionsRef = useRef(positions);
  const selectedIdsRef = useRef(selectedIds);
  const layoutMetricsRef = useRef(layoutMetrics);
  const canvasSizeRef = useRef({
    width: layoutMetrics.minWidth,
    height: layoutMetrics.minHeight,
  });

  useEffect(() => {
    positionsRef.current = positions;
  }, [positions]);

  // Before paint, so the view doesn't jump: the middle of the view stays put.
  useLayoutEffect(() => {
    zoomRef.current = zoom;
    const anchor = zoomAnchorRef.current;
    const scroller = scrollerRef.current;
    if (!anchor || !scroller) return;
    zoomAnchorRef.current = null;
    scroller.scrollLeft = anchor.x * zoom - scroller.clientWidth / 2;
    scroller.scrollTop = anchor.y * zoom - scroller.clientHeight / 2;
  }, [zoom]);

  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  useEffect(() => {
    persistedPositionsRef.current = Object.fromEntries(
      (teamMembers || []).map((member, index) => [
        member.id,
        member.orgChart || getDefaultOrgChartPosition(index, memberCount),
      ]),
    );
  }, [memberCount, teamMembers]);

  useEffect(() => {
    layoutMetricsRef.current = layoutMetrics;
  }, [layoutMetrics]);

  useEffect(() => {
    setPositions((prev) => {
      const next = {};
      (teamMembers || []).forEach((member, index) => {
        next[member.id] =
          member.orgChart ||
          prev[member.id] ||
          getDefaultOrgChartPosition(index, memberCount);
      });
      return next;
    });
  }, [memberCount, teamMembers]);

  const canvasSize = useMemo(() => {
    const extraWidth =
      layoutMetrics.paddingX + Math.max(layoutMetrics.columnGap, 88);
    const extraHeight =
      layoutMetrics.paddingY + Math.max(layoutMetrics.rowGap, 72);
    let maxX = layoutMetrics.paddingX;
    let maxY = layoutMetrics.paddingY;

    (teamMembers || []).forEach((member, index) => {
      const position =
        positions[member.id] ||
        member.orgChart ||
        getDefaultOrgChartPosition(index, memberCount);
      maxX = Math.max(maxX, Number(position?.x) || layoutMetrics.paddingX);
      maxY = Math.max(maxY, Number(position?.y) || layoutMetrics.paddingY);
    });

    return {
      width: Math.min(
        ORG_CHART_DIMENSIONS.workspaceWidth,
        Math.max(
          layoutMetrics.minWidth,
          Math.round(maxX + layoutMetrics.nodeWidth + extraWidth),
        ),
      ),
      height: Math.min(
        ORG_CHART_DIMENSIONS.workspaceHeight,
        Math.max(
          layoutMetrics.minHeight,
          Math.round(maxY + layoutMetrics.nodeHeight + extraHeight),
        ),
      ),
    };
  }, [layoutMetrics, memberCount, positions, teamMembers]);

  useEffect(() => {
    canvasSizeRef.current = canvasSize;
  }, [canvasSize]);

  useEffect(() => {
    if (!selectedId || teamMembers.some((member) => member.id === selectedId)) {
      return;
    }
    onSelect(teamMembers[0]?.id || null);
  }, [onSelect, selectedId, teamMembers]);

  useEffect(() => {
    const validIds = new Set((teamMembers || []).map((member) => member.id));
    setSelectedIds((current) => {
      const filtered = current.filter((id) => validIds.has(id));
      if (selectedId && validIds.has(selectedId)) {
        if (filtered.includes(selectedId)) return filtered;
        return [selectedId];
      }
      return filtered;
    });
  }, [selectedId, teamMembers]);

  const selectedMember = useMemo(
    () => teamMembers.find((member) => member.id === selectedId) || null,
    [selectedId, teamMembers],
  );
  const canEditSelectedMember = !!(
    selectedMember && editableMemberIdSet.has(selectedMember.id)
  );
  const topLevelCount = useMemo(
    () =>
      (teamMembers || []).filter(
        (member) =>
          !Array.isArray(member?.reportsTo) || member.reportsTo.length === 0,
      ).length,
    [teamMembers],
  );

  const connectorObstacles = useMemo(
    () =>
      (teamMembers || []).map((member, index) => {
        const position =
          positions[member.id] ||
          member.orgChart ||
          getDefaultOrgChartPosition(index, memberCount);
        return createConnectorObstacle(member.id, position, layoutMetrics);
      }),
    [layoutMetrics, memberCount, positions, teamMembers],
  );

  const edges = useMemo(() => {
    const byId = new Map((teamMembers || []).map((member) => [member.id, member]));
    return (teamMembers || [])
      .flatMap((member) =>
        (member.reportsTo || []).map((managerId) => {
          const manager = byId.get(managerId);
          if (!manager) return null;
          const sourcePos = positions[manager.id] || manager.orgChart;
          const targetPos = positions[member.id] || member.orgChart;
          if (!sourcePos || !targetPos) return null;
          const obstacles = connectorObstacles.filter(
            (obstacle) =>
              obstacle.id !== manager.id && obstacle.id !== member.id,
          );
          return {
            key: `${manager.id}:${member.id}`,
            path: buildConnectorPath(
              sourcePos,
              targetPos,
              layoutMetrics,
              obstacles,
              canvasSize,
            ),
          };
        }),
      )
      .filter(Boolean);
  }, [canvasSize, connectorObstacles, layoutMetrics, positions, teamMembers]);

  const commitPositionsIfNeeded = useCallback((nextPositionsById, force = false) => {
    const entries = Object.entries(nextPositionsById || {}).filter(
      ([memberId, nextPosition]) => memberId && nextPosition,
    );
    if (!entries.length) return;
    const changedEntries = force
      ? entries
      : entries.filter(([memberId, nextPosition]) => {
          const previousPosition = persistedPositionsRef.current[memberId];
          return !positionsMatch(previousPosition, nextPosition);
        });
    if (!changedEntries.length) return;
    const changedPositions = Object.fromEntries(changedEntries);
    persistedPositionsRef.current = {
      ...persistedPositionsRef.current,
      ...changedPositions,
    };
    if (onCommitPositions) {
      onCommitPositions(changedPositions);
      return;
    }
    changedEntries.forEach(([memberId, nextPosition]) => {
      onCommitPosition?.(memberId, nextPosition);
    });
  }, [onCommitPosition, onCommitPositions]);

  const resolveSnap = useCallback((position, memberId, movingMemberIds = []) => {
    const metrics = layoutMetricsRef.current;
    const movingAnchors = getAlignmentAnchors(position, metrics);
    const movingIds = new Set(movingMemberIds);
    let bestVertical = null;
    let bestHorizontal = null;

    (teamMembers || []).forEach((member, index) => {
      if (member.id === memberId || movingIds.has(member.id)) return;
      const otherPosition =
        positionsRef.current[member.id] ||
        member.orgChart ||
        getDefaultOrgChartPosition(index, memberCount);
      const otherAnchors = getAlignmentAnchors(otherPosition, metrics);

      [
        ["left", "left"],
        ["centerX", "centerX"],
        ["right", "right"],
      ].forEach(([sourceAnchor, targetAnchor]) => {
        const diff = otherAnchors[targetAnchor] - movingAnchors[sourceAnchor];
        if (Math.abs(diff) > SNAP_THRESHOLD) return;
        if (!bestVertical || Math.abs(diff) < Math.abs(bestVertical.diff)) {
          bestVertical = {
            diff,
            sourceAnchor,
            targetCoord: otherAnchors[targetAnchor],
            otherPosition,
          };
        }
      });

      [
        ["top", "top"],
        ["centerY", "centerY"],
        ["bottom", "bottom"],
      ].forEach(([sourceAnchor, targetAnchor]) => {
        const diff = otherAnchors[targetAnchor] - movingAnchors[sourceAnchor];
        if (Math.abs(diff) > SNAP_THRESHOLD) return;
        if (!bestHorizontal || Math.abs(diff) < Math.abs(bestHorizontal.diff)) {
          bestHorizontal = {
            diff,
            sourceAnchor,
            targetCoord: otherAnchors[targetAnchor],
            otherPosition,
          };
        }
      });
    });

    const snapped = {
      x: position.x + (bestVertical?.diff || 0),
      y: position.y + (bestHorizontal?.diff || 0),
    };
    const clamped = clampNodePosition(
      snapped,
      canvasSizeRef.current,
      layoutMetricsRef.current,
    );
    const clampedAnchors = getAlignmentAnchors(clamped, metrics);
    const guides = [];
    let bestHorizontalSpacing = null;
    let bestVerticalSpacing = null;

    const otherNodes = (teamMembers || [])
      .filter((member) => member.id !== memberId && !movingIds.has(member.id))
      .map((member, index) => {
        const otherPosition =
          positionsRef.current[member.id] ||
          member.orgChart ||
          getDefaultOrgChartPosition(index, memberCount);
        return {
          member,
          position: otherPosition,
          anchors: getAlignmentAnchors(otherPosition, metrics),
        };
      });
    const draggedNode = {
      member: { id: memberId },
      position: clamped,
      anchors: clampedAnchors,
      isDragged: true,
    };

    if (
      bestVertical &&
      Math.abs(clampedAnchors[bestVertical.sourceAnchor] - bestVertical.targetCoord) <= 1
    ) {
      guides.push({
        orientation: "vertical",
        axis: Math.round(bestVertical.targetCoord),
        start: Math.round(
          Math.min(clamped.y, bestVertical.otherPosition.y) - GUIDE_PADDING,
        ),
        end: Math.round(
          Math.max(
            clamped.y + metrics.nodeHeight,
            bestVertical.otherPosition.y + metrics.nodeHeight,
          ) + GUIDE_PADDING,
        ),
      });
    }

    if (
      bestHorizontal &&
      Math.abs(clampedAnchors[bestHorizontal.sourceAnchor] - bestHorizontal.targetCoord) <= 1
    ) {
      guides.push({
        orientation: "horizontal",
        axis: Math.round(bestHorizontal.targetCoord),
        start: Math.round(
          Math.min(clamped.x, bestHorizontal.otherPosition.x) - GUIDE_PADDING,
        ),
        end: Math.round(
          Math.max(
            clamped.x + metrics.nodeWidth,
            bestHorizontal.otherPosition.x + metrics.nodeWidth,
          ) + GUIDE_PADDING,
        ),
      });
    }

    const horizontalNodes = [...otherNodes, draggedNode].sort(
      (left, right) => left.position.x - right.position.x,
    );
    for (let index = 0; index <= horizontalNodes.length - 3; index += 1) {
      const trio = horizontalNodes.slice(index, index + 3);
      if (!trio.some((node) => node.isDragged)) continue;
      const overlapTop = Math.max(...trio.map((node) => node.anchors.top));
      const overlapBottom = Math.min(...trio.map((node) => node.anchors.bottom));
      if (overlapBottom - overlapTop < MIN_SPACING_OVERLAP) continue;
      const gapOne = trio[1].anchors.left - trio[0].anchors.right;
      const gapTwo = trio[2].anchors.left - trio[1].anchors.right;
      if (gapOne < 0 || gapTwo < 0) continue;
      const diff = gapOne - gapTwo;
      if (
        Math.abs(diff) <= SPACING_THRESHOLD &&
        (!bestHorizontalSpacing || Math.abs(diff) < Math.abs(bestHorizontalSpacing.diff))
      ) {
        bestHorizontalSpacing = {
          diff,
          axis: Math.round((overlapTop + overlapBottom) / 2),
          segments: [
            {
              start: trio[0].anchors.right,
              end: trio[1].anchors.left,
            },
            {
              start: trio[1].anchors.right,
              end: trio[2].anchors.left,
            },
          ],
        };
      }
    }

    const verticalNodes = [...otherNodes, draggedNode].sort(
      (top, bottom) => top.position.y - bottom.position.y,
    );
    for (let index = 0; index <= verticalNodes.length - 3; index += 1) {
      const trio = verticalNodes.slice(index, index + 3);
      if (!trio.some((node) => node.isDragged)) continue;
      const overlapLeft = Math.max(...trio.map((node) => node.anchors.left));
      const overlapRight = Math.min(...trio.map((node) => node.anchors.right));
      if (overlapRight - overlapLeft < MIN_SPACING_OVERLAP) continue;
      const gapOne = trio[1].anchors.top - trio[0].anchors.bottom;
      const gapTwo = trio[2].anchors.top - trio[1].anchors.bottom;
      if (gapOne < 0 || gapTwo < 0) continue;
      const diff = gapOne - gapTwo;
      if (
        Math.abs(diff) <= SPACING_THRESHOLD &&
        (!bestVerticalSpacing || Math.abs(diff) < Math.abs(bestVerticalSpacing.diff))
      ) {
        bestVerticalSpacing = {
          diff,
          axis: Math.round((overlapLeft + overlapRight) / 2),
          segments: [
            {
              start: trio[0].anchors.bottom,
              end: trio[1].anchors.top,
            },
            {
              start: trio[1].anchors.bottom,
              end: trio[2].anchors.top,
            },
          ],
        };
      }
    }

    if (bestHorizontalSpacing) {
      bestHorizontalSpacing.segments.forEach((segment) => {
        guides.push({
          orientation: "horizontal",
          axis: bestHorizontalSpacing.axis,
          start: Math.round(segment.start),
          end: Math.round(segment.end),
          variant: "spacing",
        });
      });
    }

    if (bestVerticalSpacing) {
      bestVerticalSpacing.segments.forEach((segment) => {
        guides.push({
          orientation: "vertical",
          axis: bestVerticalSpacing.axis,
          start: Math.round(segment.start),
          end: Math.round(segment.end),
          variant: "spacing",
        });
      });
    }

    return { position: clamped, guides };
  }, [memberCount, teamMembers]);

  useEffect(() => {
    if (!draggingIds.length) return undefined;

    function onPointerMove(event) {
      const state = dragRef.current;
      if (!state) return;
      if (state.pointerId != null && event.pointerId !== state.pointerId) return;
      const { position: nextAnchor, guides } = resolveSnap(
        {
          x: state.origin.x + Math.round((event.clientX - state.startClientX) / zoomRef.current),
          y: state.origin.y + Math.round((event.clientY - state.startClientY) / zoomRef.current),
        },
        state.id,
        state.memberIds,
      );
      const rawDelta = {
        x: nextAnchor.x - state.origin.x,
        y: nextAnchor.y - state.origin.y,
      };
      const delta = clampGroupDelta(
        rawDelta,
        state.originsById,
        canvasSizeRef.current,
        layoutMetricsRef.current,
      );
      const nextPositionsById = Object.fromEntries(
        state.memberIds.map((memberId) => [
          memberId,
          {
            x: state.originsById[memberId].x + delta.x,
            y: state.originsById[memberId].y + delta.y,
          },
        ]),
      );
      if (
        !state.moved &&
        (Math.abs(event.clientX - state.startClientX) > 2 ||
          Math.abs(event.clientY - state.startClientY) > 2)
      ) {
        state.moved = true;
      }
      setAlignmentGuides(guides);
      setPositions((prev) => ({ ...prev, ...nextPositionsById }));
      const now =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      if (now - lastDragPersistAtRef.current >= DRAG_PERSIST_INTERVAL_MS) {
        lastDragPersistAtRef.current = now;
        commitPositionsIfNeeded(nextPositionsById);
      }
    }

    function stopDrag(event) {
      const state = dragRef.current;
      if (!state) return;
      if (
        state.pointerId != null &&
        event?.pointerId != null &&
        event.pointerId !== state.pointerId
      ) {
        return;
      }
      const nextPositionsById = Object.fromEntries(
        state.memberIds
          .map((memberId) => [memberId, positionsRef.current[memberId]])
          .filter(([, position]) => position),
      );
      dragRef.current = null;
      setDraggingIds([]);
      setAlignmentGuides([]);
      if (state.moved) suppressClickRef.current = state.id;
      commitPositionsIfNeeded(nextPositionsById, true);
    }

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", stopDrag);
    window.addEventListener("pointercancel", stopDrag);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", stopDrag);
      window.removeEventListener("pointercancel", stopDrag);
    };
  }, [commitPositionsIfNeeded, draggingIds, resolveSnap]);

  const applySelection = useCallback((memberIds, primaryId = null, options = {}) => {
    const { clearLink = false } = options;
    const nextIds = Array.isArray(memberIds)
      ? memberIds.filter(Boolean)
      : [];
    setSelectedIds(nextIds);
    onSelect(primaryId || null);
    if (clearLink) setLinkSourceId(null);
  }, [onSelect]);

  const clearSelection = useCallback(() => {
    applySelection([], null, { clearLink: true });
  }, [applySelection]);

  const selectSingleMember = useCallback((memberId) => {
    applySelection(memberId ? [memberId] : [], memberId || null);
  }, [applySelection]);

  const toggleSelectedMember = useCallback((memberId) => {
    const current = selectedIdsRef.current.filter((id) =>
      teamMembers.some((member) => member.id === id),
    );
    const alreadySelected = current.includes(memberId);
    const nextSelection = alreadySelected
      ? current.filter((id) => id !== memberId)
      : [...current, memberId];
    const safeNextSelection = nextSelection.length ? nextSelection : [memberId];
    applySelection(
      safeNextSelection,
      alreadySelected
        ? safeNextSelection[safeNextSelection.length - 1] || null
        : memberId,
    );
  }, [applySelection, teamMembers]);

  const membersInsideMarquee = useCallback((rect) => {
    if (!rect) return [];
    return (teamMembers || [])
      .filter((member, index) => {
        const position =
          positionsRef.current[member.id] ||
          member.orgChart ||
          getDefaultOrgChartPosition(index, memberCount);
        return rectsOverlap(rect, {
          left: position.x,
          top: position.y,
          right: position.x + layoutMetricsRef.current.nodeWidth,
          bottom: position.y + layoutMetricsRef.current.nodeHeight,
        });
      })
      .map((member) => member.id);
  }, [memberCount, teamMembers]);

  function startDrag(event, memberId) {
    if (!canEditDirectory) return;
    if (event.button !== 0) return;
    if (event.target.closest("button")) return;
    if (isMultiSelectGesture(event)) return;
    const currentSelection = selectedIdsRef.current.filter((id) =>
      teamMembers.some((member) => member.id === id),
    );
    const memberIds =
      currentSelection.includes(memberId) && currentSelection.length > 1
        ? currentSelection
        : [memberId];
    const originsById = Object.fromEntries(
      memberIds.map((id, index) => {
        const member = teamMembers.find((item) => item.id === id);
        return [
          id,
          positionsRef.current[id] ||
            member?.orgChart ||
            getDefaultOrgChartPosition(index, memberCount),
        ];
      }),
    );
    const position = originsById[memberId];
    if (memberIds.length > 1) {
      setSelectedIds(memberIds);
      onSelect(memberId);
    } else {
      selectSingleMember(memberId);
    }
    dragRef.current = {
      id: memberId,
      memberIds,
      originsById,
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      origin: position,
      moved: false,
    };
    lastDragPersistAtRef.current = 0;
    setDraggingIds(memberIds);
  }

  function startMarqueeSelection(event) {
    if (!canEditDirectory) return;
    if (event.button !== 0) return;
    if (dragRef.current) return;
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    const start = getCanvasPoint(event, event.currentTarget, zoomRef.current);
    marqueeRef.current = {
      pointerId: event.pointerId,
      start,
      additive: isMultiSelectGesture(event),
      baseSelection: selectedIdsRef.current,
      moved: false,
    };
    setMarqueeRect({
      left: start.x,
      top: start.y,
      width: 0,
      height: 0,
    });
  }

  function changeZoom(nextPercent) {
    const next = cleanOrgChartZoom(nextPercent);
    if (next === zoomPercent) return;
    const scroller = scrollerRef.current;
    if (scroller) {
      zoomAnchorRef.current = {
        x: (scroller.scrollLeft + scroller.clientWidth / 2) / zoom,
        y: (scroller.scrollTop + scroller.clientHeight / 2) / zoom,
      };
    }
    setZoomPercent(next);
    saveOrgChartZoom(next);
  }

  function toggleLinkMode(memberId) {
    if (!canEditDirectory) return;
    setLinkSourceId((current) => (current === memberId ? null : memberId));
  }

  function swallowPointer(event) {
    event.stopPropagation();
  }

  function handleNodeClick(event, memberId) {
    if (suppressClickRef.current === memberId) {
      suppressClickRef.current = null;
      return;
    }
    if (linkSourceId && linkSourceId !== memberId) {
      onToggleReportingLine?.(linkSourceId, memberId);
      setLinkSourceId(null);
      selectSingleMember(memberId);
      return;
    }
    if (isMultiSelectGesture(event)) {
      toggleSelectedMember(memberId);
      return;
    }
    const currentSelection = selectedIdsRef.current;
    if (currentSelection.length > 1 && currentSelection.includes(memberId)) {
      onSelect(memberId);
      return;
    }
    selectSingleMember(memberId);
  }

  function handleCanvasClick(event) {
    if (event.target !== event.currentTarget) return;
    if (suppressClickRef.current === "__canvas__") {
      suppressClickRef.current = null;
      return;
    }
    clearSelection();
  }

  useEffect(() => {
    if (!marqueeRect) return undefined;

    function onPointerMove(event) {
      const state = marqueeRef.current;
      if (!state) return;
      if (state.pointerId != null && event.pointerId !== state.pointerId) return;
      const activeCanvas = canvasRef.current;
      if (!activeCanvas) return;
      const point = getCanvasPoint(event, activeCanvas, zoomRef.current);
      const rect = normalizeRect(state.start, point);
      const width = rect.right - rect.left;
      const height = rect.bottom - rect.top;
      if (
        !state.moved &&
        (Math.abs(point.x - state.start.x) >= MARQUEE_START_THRESHOLD ||
          Math.abs(point.y - state.start.y) >= MARQUEE_START_THRESHOLD)
      ) {
        state.moved = true;
      }
      setMarqueeRect({
        left: rect.left,
        top: rect.top,
        width,
        height,
      });
      const nextSelection = membersInsideMarquee(rect);
      const selection = state.additive
        ? Array.from(new Set([...state.baseSelection, ...nextSelection]))
        : nextSelection;
      const primaryId = selection[selection.length - 1] || null;
      applySelection(selection, primaryId, { clearLink: true });
    }

    function stopMarquee(event) {
      const state = marqueeRef.current;
      if (!state) return;
      if (
        state.pointerId != null &&
        event?.pointerId != null &&
        event.pointerId !== state.pointerId
      ) {
        return;
      }
      marqueeRef.current = null;
      setMarqueeRect(null);
      if (state.moved) suppressClickRef.current = "__canvas__";
      if (!state.moved && !state.additive) {
        clearSelection();
      }
    }

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", stopMarquee);
    window.addEventListener("pointercancel", stopMarquee);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", stopMarquee);
      window.removeEventListener("pointercancel", stopMarquee);
    };
  }, [applySelection, clearSelection, marqueeRect, membersInsideMarquee]);

  if (!teamMembers.length) {
    return (
      <div className="orgChartShell" style={{ marginTop: 14 }}>
        <div className="orgChartEmpty">
          <div className="itemTitle">No teammates yet</div>
          <div className="muted" style={{ marginTop: 6 }}>
            Start the directory directly from the visual chart.
          </div>
          <button
            type="button"
            className="btn primary"
            style={{ marginTop: 14 }}
            onClick={onOpenCreate}
            disabled={!canEditDirectory}
          >
            Add first teammate
          </button>
        </div>
      </div>
    );
  }

  const selectedStatus = selectedMember ? statusById?.[selectedMember.id] : null;
  const hasMultiSelection = selectedIds.length > 1;
  const selectionTitle = selectedMember
    ? hasMultiSelection
      ? `${selectedIds.length} teammates selected`
      : getMemberLabel(selectedMember)
    : "Organization overview";
  const selectionSummary = selectedMember
    ? hasMultiSelection
      ? `Primary teammate: ${getMemberLabel(selectedMember)}. Drag any selected card to move the group together.`
      : getOrgSummary?.(selectedMember, teamMembers)
    : `${teamMembers.length} teammates in the directory, ${topLevelCount} top-level org ${topLevelCount === 1 ? "node" : "nodes"}. Click a teammate to inspect details, or box-select to move a group without shifting the chart.`;

  return (
    <div className="orgChartShell" style={{ marginTop: 14 }}>
      <div className="orgChartToolbar">
        <div className="orgChartToolbar__intro">
          <div className="itemTitle">Visual org chart</div>
          <div className="muted small" style={{ marginTop: 4 }}>
            Drag to place teammates. Use link mode to connect reporting lines.
            Double-click any person to edit their details. Shift/Ctrl/Cmd-click
            to multi-select and drag together, or drag on empty space to box-select.
          </div>
        </div>
        <div className="orgChartToolbar__actions">
          <button
            className="btn primary"
            type="button"
            onClick={onOpenCreate}
            disabled={!canEditDirectory}
          >
            Add teammate
          </button>
          <button
            className="btn"
            type="button"
            onClick={onAutoLayout}
            disabled={!canEditDirectory}
          >
            Auto-layout
          </button>
        </div>
      </div>

      <div
        className={`orgChartSelection ${selectedMember ? "" : "empty"}`.trim()}
      >
        <div className="bdAvatarRow bdAvatarRow--top">
          {selectedMember && !hasMultiSelection ? (
            <Avatar
              name={selectedMember.name}
              email={selectedMember.email}
              src={getPersonPhotoUrl(selectedMember)}
              size={40}
            />
          ) : null}
          <div className="orgChartSelection__copy">
            <strong>{selectionTitle}</strong>
            <span className="muted">{selectionSummary}</span>
            {selectedMember && linkSourceId === selectedMember.id ? (
              <span className="orgChartNotice">
                Click the teammate who should report to{" "}
                {getMemberLabel(selectedMember)}.
              </span>
            ) : null}
            {selectedStatus?.msg ? (
              <span
                className="muted"
                style={{
                  color:
                    selectedStatus.ok === false
                      ? "var(--danger, #ff9a9a)"
                      : selectedStatus.ok === true
                        ? "var(--success, #8ee59b)"
                        : "inherit",
                }}
              >
                {selectedStatus.msg}
              </span>
            ) : null}
          </div>
        </div>

        {selectedMember ? (
          <div className="orgChartSelection__actions">
            {canEditSelectedMember ? (
              <button
                type="button"
                className="btn"
                onClick={() => onOpenEdit?.(selectedMember.id)}
              >
                Edit details
              </button>
            ) : null}
            {canEditDirectory ? (
              <button
                type="button"
                className="btn"
                onClick={() => toggleLinkMode(selectedMember.id)}
              >
                {linkSourceId === selectedMember.id
                  ? "Cancel link"
                  : "Link direct report"}
              </button>
            ) : null}
            {/* Gated on canSendReports (owner OR admin), NOT canEditDirectory:
                sending the exec report hands out no access, and the server
                route is requireWorkspaceAdmin. */}
            {canSendReports && selectedMember.execRecipient ? (
              <button
                type="button"
                className="btn"
                onClick={() => onSendNow?.(selectedMember.id)}
                disabled={sendingId === selectedMember.id}
                aria-busy={sendingId === selectedMember.id}
              >
                {sendingId === selectedMember.id ? "Sending..." : "Send now"}
              </button>
            ) : null}
            {canEditDirectory ? (
              <button
                type="button"
                className="btn btnDangerGhost"
                onClick={() => onDeleteMember?.(selectedMember.id)}
              >
                Delete
              </button>
            ) : null}
          </div>
        ) : (
          <div className="orgChartSelection__actions orgChartSelection__actions--empty">
            <span className="muted">No teammate selected</span>
          </div>
        )}
      </div>

      <div className="orgChartViewport">
        <div className="orgChartZoom" role="group" aria-label="Chart zoom">
          <button
            type="button"
            aria-label="Zoom out"
            title="Zoom out"
            disabled={zoomPercent <= ORG_CHART_ZOOM_MIN}
            onClick={() => changeZoom(zoomPercent - ORG_CHART_ZOOM_STEP)}
          >
            {"\u2212"}
          </button>
          <button
            type="button"
            className="orgChartZoom__value"
            aria-label={`Zoom is ${zoomPercent}%. Press to go back to 100%.`}
            title="Back to 100%"
            onClick={() => changeZoom(ORG_CHART_ZOOM_DEFAULT)}
          >
            {zoomPercent}%
          </button>
          <button
            type="button"
            aria-label="Zoom in"
            title="Zoom in"
            disabled={zoomPercent >= ORG_CHART_ZOOM_MAX}
            onClick={() => changeZoom(zoomPercent + ORG_CHART_ZOOM_STEP)}
          >
            +
          </button>
        </div>
        <div className="orgChartScroller" ref={scrollerRef}>
          {/* The scale doesn't change layout, so this holds the scroll area to what's on screen. */}
          <div
            className="orgChartSizer"
            style={{
              width: canvasSize.width * zoom,
              height: canvasSize.height * zoom,
            }}
          >
        <div
          ref={canvasRef}
          className="orgChartCanvas"
          onPointerDown={startMarqueeSelection}
          onClick={handleCanvasClick}
          style={{
            width: canvasSize.width,
            height: canvasSize.height,
            ...(zoom === 1 ? {} : { transform: `scale(${zoom})` }),
            "--org-node-width": `${layoutMetrics.nodeWidth}px`,
            "--org-node-height": `${layoutMetrics.nodeHeight}px`,
            "--org-avatar-size": `${layoutMetrics.avatarSize}px`,
          }}
        >
          {marqueeRect ? (
            <div
              className="orgChartMarquee"
              style={{
                left: marqueeRect.left,
                top: marqueeRect.top,
                width: marqueeRect.width,
                height: marqueeRect.height,
              }}
            />
          ) : null}
          <svg
            className="orgChartSvg"
            width={canvasSize.width}
            height={canvasSize.height}
            aria-hidden="true"
          >
            <defs>
              <marker
                id={markerIdRef.current}
                markerWidth="8"
                markerHeight="8"
                refX="6.1"
                refY="4"
                orient="auto"
                markerUnits="userSpaceOnUse"
              >
                <path d="M1.1 1 L6.1 4 L1.1 7 Z" fill="rgba(255, 247, 203, 0.88)" />
              </marker>
            </defs>
            {edges.map((edge) => (
              <path
                key={edge.key}
                d={edge.path}
                className="orgChartEdge"
                markerEnd={`url(#${markerIdRef.current})`}
              />
            ))}
            {alignmentGuides.map((guide) => {
              const key = `guide-${guide.orientation}-${guide.variant || "align"}-${guide.axis}-${guide.start}-${guide.end}`;
              if (guide.variant === "spacing") {
                return guide.orientation === "vertical" ? (
                  <g key={key} className="orgChartGuide spacing">
                    <line
                      x1={guide.axis}
                      x2={guide.axis}
                      y1={guide.start}
                      y2={guide.end}
                    />
                    <line
                      x1={guide.axis - SPACING_TICK_SIZE}
                      x2={guide.axis + SPACING_TICK_SIZE}
                      y1={guide.start}
                      y2={guide.start}
                    />
                    <line
                      x1={guide.axis - SPACING_TICK_SIZE}
                      x2={guide.axis + SPACING_TICK_SIZE}
                      y1={guide.end}
                      y2={guide.end}
                    />
                  </g>
                ) : (
                  <g key={key} className="orgChartGuide spacing">
                    <line
                      x1={guide.start}
                      x2={guide.end}
                      y1={guide.axis}
                      y2={guide.axis}
                    />
                    <line
                      x1={guide.start}
                      x2={guide.start}
                      y1={guide.axis - SPACING_TICK_SIZE}
                      y2={guide.axis + SPACING_TICK_SIZE}
                    />
                    <line
                      x1={guide.end}
                      x2={guide.end}
                      y1={guide.axis - SPACING_TICK_SIZE}
                      y2={guide.axis + SPACING_TICK_SIZE}
                    />
                  </g>
                );
              }

              return guide.orientation === "vertical" ? (
                <line
                  key={key}
                  x1={guide.axis}
                  x2={guide.axis}
                  y1={guide.start}
                  y2={guide.end}
                  className="orgChartGuide"
                />
              ) : (
                <line
                  key={key}
                  x1={guide.start}
                  x2={guide.end}
                  y1={guide.axis}
                  y2={guide.axis}
                  className="orgChartGuide"
                />
              );
            })}
          </svg>

          {teamMembers.map((member, index) => {
            const position =
              positions[member.id] ||
              member.orgChart ||
              getDefaultOrgChartPosition(index, memberCount);
            const directReports = getDirectReports(member.id, teamMembers);
            const memberLabel = getMemberLabel(member);
            const roleLabel = member.role || "Role not set";
            const emailLabel = member.email || "No email yet";
            const reportLabel = directReports.length
              ? `${directReports.length} report${directReports.length === 1 ? "" : "s"}`
              : "No reports";
            const timezoneLabel = getTimeZoneLabel(member.tz);
            const memberReconnects =
              reconnectByEmail[String(member.email || "").toLowerCase()] || [];
            const primaryReconnect = memberReconnects[0] || null;
            // The card is only ~202px wide inside its padding, so the badge has
            // to stay short — the provider names ("Microsoft (Teams / Outlook /
            // Calendar)") live in the tooltip instead of the label.
            const reconnectLabel = primaryReconnect
              ? `Reconnect${
                  memberReconnects.length > 1
                    ? ` +${memberReconnects.length - 1}`
                    : ""
                }`
              : "";
            const reconnectTooltip = memberReconnects
              .map((item) => item.label || item.provider || "Connection")
              .join(", ");

            return (
              <div
                key={member.id}
                className={`orgNode ${selectedIds.includes(member.id) ? "selected" : ""} ${draggingIds.includes(member.id) ? "dragging" : ""} ${linkSourceId === member.id ? "linking" : ""}`}
                style={{
                  transform: `translate(${position.x}px, ${position.y}px)`,
                }}
                onPointerDown={(event) => startDrag(event, member.id)}
                onClick={(event) => handleNodeClick(event, member.id)}
                onDoubleClick={() => {
                  if (editableMemberIdSet.has(member.id)) {
                    onOpenEdit?.(member.id);
                  }
                }}
              >
                <div className="orgNode__hero">
                  <Avatar
                    name={member.name}
                    email={member.email}
                    src={getPersonPhotoUrl(member)}
                    size={layoutMetrics.avatarSize}
                  />
                  <div className="orgNode__meta">
                    <div className="orgNode__header">
                      <div className="orgNode__title" title={memberLabel}>
                        {memberLabel}
                      </div>
                      <div className="orgNode__headerActions">
                        {member.execRecipient ? (
                          <span className="orgChartPill">Exec</span>
                        ) : null}
                        {editableMemberIdSet.has(member.id) ? (
                          <button
                            type="button"
                            className="orgNode__iconButton"
                            aria-label={`Edit ${memberLabel}`}
                            title={`Edit ${memberLabel}`}
                            onPointerDown={swallowPointer}
                            onClick={(event) => {
                              event.stopPropagation();
                              onSelect(member.id);
                              onOpenEdit?.(member.id);
                            }}
                          >
                            <PencilIcon />
                          </button>
                        ) : null}
                      </div>
                    </div>
                    <div className="orgNode__subtitle" title={roleLabel}>
                      {roleLabel}
                    </div>
                  </div>
                </div>

                <div className="orgNode__email" title={emailLabel}>
                  {emailLabel}
                </div>

                <div className="orgNode__pillRow">
                  <span className="orgChartPill">
                    {getTeamSecurityLabel(member)}
                  </span>
                  {!primaryReconnect ? (
                    <span className="orgChartPill subtle">
                      {member.reportsTo?.length ? "Reports up" : "Top level"}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="orgChartPill"
                      title={`Sign-in expired. Click to reconnect ${reconnectTooltip}`}
                      onPointerDown={swallowPointer}
                      onClick={(event) => {
                        event.stopPropagation();
                        window.open(
                          `${apiBase}${primaryReconnect.reconnectPath}?email=${encodeURIComponent(
                            primaryReconnect.account || member.email || "",
                          )}&returnTo=${encodeURIComponent(
                            window.location.pathname,
                          )}`,
                          "_blank",
                          "width=560,height=760",
                        );
                      }}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        minWidth: 0,
                        maxWidth: "100%",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        cursor: "pointer",
                        background: "rgba(240,180,41,.16)",
                        color: "#f5c451",
                        border: "1px solid rgba(240,180,41,.45)",
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: 999,
                          background: "#f5c451",
                          flex: "0 0 auto",
                        }}
                      />
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {reconnectLabel}
                      </span>
                    </button>
                  )}
                </div>

                {canEditDirectory ? (
                  <div className="orgNode__actionRow">
                    <button
                      type="button"
                      className={`orgNode__actionButton ${
                        linkSourceId === member.id ? "active" : ""
                      }`}
                      onPointerDown={swallowPointer}
                      onClick={(event) => {
                        event.stopPropagation();
                        onSelect(member.id);
                        toggleLinkMode(member.id);
                      }}
                    >
                      {linkSourceId === member.id
                        ? "Cancel link"
                        : "Link direct report"}
                    </button>
                  </div>
                ) : null}

                <div className="orgNode__stats">
                  <span title={reportLabel}>{reportLabel}</span>
                  <span title={member.tz || "Timezone missing"}>{timezoneLabel}</span>
                </div>
              </div>
            );
          })}
        </div>
          </div>
        </div>
      </div>
    </div>
  );
}
